import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '../../components/ui/Button';
import { Tag } from 'primereact/tag';
import { Card } from '../ui/Card';
import { Item, ItemType, UnitType } from '../../types/item.types';
import { AppTheme } from '../../AppTheme';
import { getAllItems, deleteItem } from '../../service/item/ItemServices';
import { PaginationInputDto } from '../../model/common/PaginatedInputDto';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { PermissionVariables } from '../../shared/constant/PermissionVariables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import { showToast } from '../../store/slices/toastSlice';

export const ItemList: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    unitType: { value: null, matchMode: FilterMatchMode.EQUALS },
    industry: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'defaultTaxRate': { value: null, matchMode: FilterMatchMode.EQUALS }
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 5,
    page: 1,
    sortField: undefined,
    sortOrder: undefined
  });

  const userPermissions = useSelector((state: any) => state.permission.permissions);
  const localization = useSelector((state: any) => state.localization);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = React.useRef<Toast>(null);

  const fetchItems = async () => {
    try {

      const params: PaginationInputDto = {
        Page: lazyParams.page,
        Limit: lazyParams.rows,
        SortProperty: lazyParams.sortField,
        SortBy: lazyParams.sortOrder === 1 ? 'ASC' : 'DESC',
        Filter: globalFilterValue,
        PropertyNames: ['item_name']
      };

      if (filters.type.value) {
        params.type = filters.type.value;
      }

      if (filters.unitType.value) {
        params.unitType = filters.unitType.value;
      }

      const response = await getAllItems(dispatch, params);

      if (response) {
        setItems(response.List);
        setTotalRecords(response.TotalCount);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      dispatch(showToast({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: localization?.FailedToLoadItems || 'Failed to load items'
      }));
    } 
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchItems();
    }, 1000); // 500ms debounce

    return () => clearTimeout(debounceTimer); // Cleanup on change
  }, [globalFilterValue, filters]);

  useEffect(() => {
    fetchItems();
  }, [lazyParams]);

  const onPage = (event: any) => {
    setLazyParams(prev => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1
    }));
  };

  const onSort = (event: any) => {
    setLazyParams(prev => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder
    }));
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters['global'].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  // Item type template
  const itemTypeTemplate = (rowData: Item) => {
    const typeClasses = {
      product: 'bg-blue-100 text-blue-700',
      service: 'bg-green-100 text-green-700',
      hourly: 'bg-purple-100 text-purple-700',
      project_phase: 'bg-orange-100 text-orange-700'
    };

    const typeLabels = {
      product: localization?.Product || 'Product',
      service: localization?.Service || 'Service',
      hourly: localization?.HourlyRate || 'Hourly Rate',
      project_phase: localization?.ProjectPhase || 'Project Phase'
    };

    return (
      <Tag
        value={typeLabels[rowData.item_type as keyof typeof typeLabels]}
        className={typeClasses[rowData.item_type as keyof typeof typeClasses]}
      />
    );
  };

  // Unit type template
  const unitTypeTemplate = (rowData: Item) => {
    return <span>{rowData?.unit_type?.charAt(0).toUpperCase() + rowData?.unit_type?.slice(1)}</span>;
  };

  // Price template
  const priceTemplate = (rowData: Item) => {
    return <span className="font-medium">${parseInt(rowData?.unit_price || "0")?.toFixed(2)}</span>;
  };

  // Tax rate template
  const taxRateTemplate = (rowData: Item) => {
    return rowData.taxRate?.tax_name || '-';
  };

  const industryTemplate = (rowData: Item) => {
    return rowData.industry?.industry_name || '-';
  };

  // Actions template
  const actionsTemplate = (rowData: Item) => {
    return (
      <div className="flex gap-2">
        <Button
          className="p-button-rounded bg-transparent text-gold hover:text-white"
          onClick={() => navigate(`/item/edit/${rowData.item_id}`)}
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
        <Button
          className="p-button-rounded p-button-danger bg-transparent text-red-600 hover:bg-red-600 hover:border-red-600 hover:text-white"
          onClick={() => confirmDeleteItem(rowData)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    );
  };

  // Type filter template
  const typeFilterTemplate = (options: any) => {
    const itemTypeOptions = [
      { label: localization?.Product || 'Product', value: 'product' },
      { label: localization?.Service || 'Service', value: 'service' },
      { label: localization?.HourlyRate || 'Hourly Rate', value: 'hourly' },
      { label: localization?.ProjectPhase || 'Project Phase', value: 'project_phase' }
    ];

    return (
      <Dropdown
        value={options.value}
        options={itemTypeOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={localization?.SelectType || "Select Type"}
        className="p-column-filter"
        showClear
      />
    );
  };

  // Unit type filter template
  const unitTypeFilterTemplate = (options: any) => {
    const unitTypeOptions = [
      { label: localization?.Each || 'Each', value: 'each' },
      { label: localization?.Hour || 'Hour', value: 'hour' },
      { label: localization?.Day || 'Day', value: 'day' },
      { label: localization?.Project || 'Project', value: 'project' }
    ];

    return (
      <Dropdown
        value={options.value}
        options={unitTypeOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={localization?.SelectUnit || "Select Unit"}
        className="p-column-filter"
        showClear
      />
    );
  };

  const confirmDeleteItem = (item: Item) => {
    confirmDialog({
      message: `${localization?.AreYouSureYouWantToDelete || 'Are you sure you want to delete'} ${item.item_name}?`,
      header: localization?.ConfirmDeletion || 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => handleDeleteItem(item)
    });
  };

  const handleDeleteItem = async (item: Item) => {
    try {
      const response = await deleteItem(dispatch, item.item_id);

      if (response) {
        dispatch(showToast({
          severity: 'success',
          summary: localization?.Success || 'Success',
          detail: localization?.ItemDeletedSuccessfully || 'Item deleted successfully'
        }));
        fetchItems();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      dispatch(showToast({
        severity: 'error',
        summary: localization?.Error || 'Error',
        detail: localization?.FailedToDeleteItem || 'Failed to delete item'
      }));
    }
  };

  const header = (
    <div className="flex justify-between items-center">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={localization?.SearchItems || "Search Items"}
        />
      </span>
      {userPermissions?.includes(PermissionVariables.addItem) && <Button
        icon="pi pi-plus"
        onClick={() => navigate('/item/create')}
      >
        {localization?.AddItem || "Add Item"}
      </Button>}
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex justify-between items-center mb-0">
        <h1 className="text-xl font-bold surface-900 mb-4">{localization?.ItemsServices || "Items & Services"}</h1>
      </div>

      <Card className='card_content_padding'>
        <DataTable
          value={items}
          lazy
          paginator
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          dataKey="id"
          filters={filters}
          globalFilterFields={['name', 'code', 'description', 'industry']}
          header={header}
          emptyMessage={localization?.NoInvoicesFound || "No items found"}
          stripedRows
          className="p-datatable-sm"
          rowsPerPageOptions={[5, 10, 20, 50]}
        >
          <Column field="item_code" header={localization?.Code || "Code"} />
          <Column field="item_name" header={localization?.Name || "Name"} sortable />
          <Column field="unit_price" header={localization?.Price || "Price"} body={priceTemplate} sortable />
          <Column field="unit_type" header={localization?.Unit || "Unit"} body={unitTypeTemplate} />
          <Column body={actionsTemplate} header={localization?.Actions || "Actions"} style={{ width: '8rem', textAlign: 'center' }} />
        </DataTable>
      </Card>
    </div>
  );
};